import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Icons } from '@ohif/ui-next';

/**
 * PARCHE CABSEN (2026-09-14): lista de estudios en tarjetas para pantallas estrechas (< 768 px).
 *
 * Consume el mismo `tableDataSource` que StudyListTable. Cada celda trae en `text` (o, si no, en
 * `title`) el valor en texto plano; aqui se pinta sin truncar, para que en el telefono se lean el
 * nombre y la descripcion completos. Un toque en la tarjeta despliega el mismo `expandedContent`
 * que en la tabla (botones de los modos y tabla de series).
 */

const textoDe = (row, key) => {
  const celda = row.find(c => c.key === key);
  if (!celda) {
    return '';
  }
  const valor = celda.text ?? celda.title ?? '';
  return String(valor).trim();
};

const StudyListCard = ({ tableData }) => {
  const { t } = useTranslation('StudyList');
  const { row, expandedContent, onClickRow, isExpanded, dataCY, clickableCY } = tableData;
  const nombre = textoDe(row, 'patientName');
  const mrn = textoDe(row, 'mrn');
  const fecha = textoDe(row, 'studyDate');
  const descripcion = textoDe(row, 'description');
  const modalidad = textoDe(row, 'modality');
  const adhesion = textoDe(row, 'accession');
  const instancias = textoDe(row, 'instances');

  return (
    <div
      data-cy={dataCY}
      className={classnames('rounded border transition duration-300', {
        'border-primary-light bg-secondary-dark': isExpanded,
        'border-transparent bg-primary-dark': !isExpanded,
      })}
    >
      <div
        className="flex cursor-pointer select-none items-start gap-2 px-3 py-3"
        onClick={onClickRow}
        data-cy={clickableCY}
      >
        <div className="mt-0.5 shrink-0">
          {isExpanded ? (
            <Icons.ChevronOpen className="inline-flex" />
          ) : (
            <Icons.ChevronClosed className="inline-flex rotate-180" />
          )}
        </div>
        <div className="min-w-0 flex-1 break-words text-lg text-white">
          <div className="text-xl font-semibold">{nombre || t('Empty')}</div>
          {(mrn || adhesion) && (
            <div className="text-primary-light">
              {mrn && (
                <span>
                  {t('MRN')} {mrn}
                </span>
              )}
              {mrn && adhesion && <span> · </span>}
              {adhesion && (
                <span>
                  {t('AccessionShort')} {adhesion}
                </span>
              )}
            </div>
          )}
          {fecha && <div>{fecha}</div>}
          {descripcion && <div>{descripcion}</div>}
          {(modalidad || instancias) && (
            <div className="text-secondary-light">
              {modalidad}
              {modalidad && instancias && ' · '}
              {instancias &&
                `${instancias} ${t(instancias === '1' ? 'Instance' : 'Instances').toLowerCase()}`}
            </div>
          )}
        </div>
      </div>
      {isExpanded && <div className="select-text bg-black">{expandedContent}</div>}
    </div>
  );
};

StudyListCard.propTypes = {
  tableData: PropTypes.shape({
    row: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        text: PropTypes.string,
        title: PropTypes.string,
      })
    ).isRequired,
    expandedContent: PropTypes.node.isRequired,
    onClickRow: PropTypes.func.isRequired,
    isExpanded: PropTypes.bool.isRequired,
    dataCY: PropTypes.string,
    clickableCY: PropTypes.string,
  }),
};

const StudyListCards = ({ tableDataSource, querying }) => {
  return (
    <div className="bg-black">
      <div
        className="flex flex-col gap-2 px-3 py-2"
        data-cy="study-list-results"
        data-querying={querying}
      >
        {tableDataSource.map((tableData, i) => (
          <StudyListCard
            tableData={tableData}
            key={i}
          />
        ))}
      </div>
    </div>
  );
};

StudyListCards.propTypes = {
  tableDataSource: PropTypes.arrayOf(PropTypes.object).isRequired,
  querying: PropTypes.bool,
};

export default StudyListCards;
